import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";
import { BehaviorSubject, map, Observable } from "rxjs";
import { IAdminLoginVerifyRequest, IAdminService_GetAllLobbiesRequest, IAdminService_LoginAdminRequest, IAdminService_LoginAdminResponse, IAdminService_UpdateRevenueSettingsRequest } from "./admin.model";
import { ApiService } from "../../api/api.service";
import { AccountService } from "../../account/account.service";
import { IUserLogin } from "../../account/account.model";
import { ICreateFeaturedLobbyRequest, ICreateFeaturedLobbyResponse, ILobbiesApiResponse, ILobbiesApiData } from "../../../_models/lobbies.model";
import { IUsersApiResponse, IUserStatusUpdateReq, IUserStatusUpdateRes, IWaitlistUserReq, IUserRoleUpdateReq, IUserRoleUpdateRes } from "../../../_models/users.model";
import { environment } from "../../../../environments/environment";
import { IPagedRequest } from "../../../_models/request.model";
import { IAdminSettingsRes } from "../../../_models/settings.model";
import { IDashboardApiResponse } from "../../../_models/dashboard.model";
import { SessionService } from "../../session/session.service";


@Injectable({
  providedIn: "root",
})
export class AdminService {

  constructor(
    private apiService: ApiService,
    private sessionService: SessionService,
    private accountService: AccountService
  ) { }
  public currentUserSource = new BehaviorSubject<IUserLogin | null>(
    this.getUserFromStorage()
  );
  public currentUser$ = this.currentUserSource.asObservable();

  private getUserFromStorage(): IUserLogin | null {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        return JSON.parse(userString);
      }
    } catch (error) {
      console.error("Error parsing user from localStorage:", error);
      localStorage.removeItem("user");
    }
    return null;
  }

  loginAdmin(body: IAdminService_LoginAdminRequest) {
    return this.apiService.post<IAdminService_LoginAdminRequest, IAdminService_LoginAdminResponse>({
      Url: "/api/account/auth/main/dating/login",
      Body: body
    }).pipe(
      map(res => {
        if (res?.auth?.token && res?.data) {
          const role = res.data.role ?? undefined;
          const userLogin: IUserLogin = {
            firstName: res.data.firstName || "Admin",
            token: res.auth.token,
            role: role,
          };
          localStorage.setItem("user", JSON.stringify(userLogin));
          this.accountService.setCurrentUser(userLogin);
        }
        return res;
      })
    );
  }

  getAllLobbies(props: IAdminService_GetAllLobbiesRequest) {
    let params = new HttpParams()
      .set("page", String(props.Params.page))
      .set("limit", String(props.Params.limit))
      .set("type", String(props.Params.type))
      .set("status", String(props.Params.status))
      .set("includeDeleted", String(props.Params.includeDeleted ?? false));
    if (props.Params.search) {
      params = params.set("search", props.Params.search);
    }
    if (props.Params.date) {
      params = params.set("date", props.Params.date);
    }
    if (props.Params.sort) {
      params = params.set("sort", props.Params.sort);
    }
    if (props.Params.sortOrder) {
      params = params.set("sortOrder", props.Params.sortOrder);
    }

    return this.apiService.get<ILobbiesApiData>({
      Url: "dating/admin/lobbies",
      Params: params
    });
  }

  joinWaitlist(body: IWaitlistUserReq) {
    const data: IWaitlistUserReq = {
      firstName: body.firstName,
      lastName: body.lastName,
      email: body.email,
      isOptionalFieldEnabled: body.isOptionalFieldEnabled
    }

    if (body.isOptionalFieldEnabled) {
      data.phone = body.phone;
      data.preferredContactMethod = body.preferredContactMethod;
      data.leadSource = body.leadSource;
      data.specificQuestionsOrRequests = body.specificQuestionsOrRequests;
    }

    return this.apiService.post<IWaitlistUserReq, object>({
      Url: `external/join-app-waitlist/appId/${environment.AdoreTheBalloonDatingAppId}`,
      Body: data,
      BaseUrl: environment.insizonBackendBaseUrl
    });
  }

  getJoinWaitlist() {
    return this.apiService.getAllPageed<IWaitlistUserReq>({
      Url: `external/join-app-waitlist/appId/${environment.AdoreTheBalloonDatingAppId}`,
      BaseUrl: environment.insizonBackendBaseUrl
    });
  }

  getUserById(id: string) {
    return this.apiService.get<any>({
      Url: `dating/admin/users/${id}`
    });
  }
  
  getAllUsers(req: IPagedRequest) {
    let params = new HttpParams({
      fromObject: {
        page: String(req.page),
        limit: String(req.limit),
        ...(req.search && { search: req.search }),
        ...(req.sort && { sortBy: req.sort }),
        ...(req.sortOrder && { sortOrder: req.sortOrder }),
        ...(req.includeDeleted && { includeDeleted: 'true' }),
        ...(req.subscriptionType && { subscriptionType: req.subscriptionType }),
        ...(req.subscriptionStatus && { subscriptionStatus: req.subscriptionStatus })
      },
    });

    return this.apiService.get<IUsersApiResponse>({
      Url: "dating/admin/users",
      Params: params
    });
  }

  updateUserStatus(
    userId: string,
    isActive?: boolean,
    accountStatus?: string,
    suspendedUntil?: string,
    reason?: string,
    sendEmail?: boolean,
    emailSubject?: string,
    emailDescription?: string,
    suspendedServices?: string[]
  ) {
    return this.apiService.put<any, any>({
      Url: "dating/admin/users/status",
      Body: {
        userId: userId,
        isActive: isActive,
        accountStatus: accountStatus,
        suspendedUntil: suspendedUntil,
        suspendedServices: suspendedServices,
        reason: reason,
        sendEmail: sendEmail,
        emailSubject: emailSubject,
        emailDescription: emailDescription
      }
    });
  }

  updateUserRole(userIds: string[], role: 'user' | 'admin' | 'super_admin') {
    return this.apiService.put<IUserRoleUpdateReq, IUserRoleUpdateRes>({
      Url: "dating/admin/users/role",
      Body: {
        userIds: userIds,
        role: role,
      }
    });
  }

  deleteUsers(userIds: string[]) {
    return this.apiService.put<{ userIds: string[] }, any>({
      Url: "dating/admin/users/delete",
      Body: { userIds }
    });
  }

  deleteLobbies(lobbyIds: string[]) {
    return this.apiService.put<{ lobbyIds: string[] }, any>({
      Url: "dating/admin/lobbies/delete",
      Body: { lobbyIds }
    });
  }

  getRevenueSettings() {
    return this.apiService.get<IAdminSettingsRes>({
      Url: "account/auth/main/dating/lobby/admin/settings"
    });
  }

  updateRevenueSettings(props: IAdminService_UpdateRevenueSettingsRequest) {
    return this.apiService.put<IAdminService_UpdateRevenueSettingsRequest, IAdminSettingsRes>({
      Url: "/account/auth/main/dating/lobby/admin/settings",
      Body: props
    });
  }

  createFeaturedLobby(
    payload: ICreateFeaturedLobbyRequest
  ): Observable<ICreateFeaturedLobbyResponse> {
    return this.apiService.post<ICreateFeaturedLobbyRequest, ICreateFeaturedLobbyResponse>({
      Url: "account/auth/main/dating/lobby/admin/featured/create",
      Body: payload
    });
  }

  getAdminDashboard(dateRange: string = "30d") {
    let params = new HttpParams().set("dateRange", String(dateRange));
    return this.apiService.get<IDashboardApiResponse>({
      Url: "dating/admin/dashboard",
      Params: params
    });
  }

  verifyAdminLogin(body: IAdminLoginVerifyRequest, rememberMe: boolean) {
    return this.apiService.post<IAdminLoginVerifyRequest, any>({
      Url: "dating/auth/admin-login-verify",
      Body: body
    }).pipe(
      map(res => {
        if (res) {
          localStorage.setItem("user", JSON.stringify(res.data));
          this.currentUserSource.next({
            firstName: res.data.firstName,
            role: res.data.role,
            token: res.auth.token,
            uuidv4Id: res.data.uuidv4Id
          });
          if (res.auth && res.auth.token) {
            try {
              this.sessionService.setSession(
                res.auth.token,
                res.data,
                rememberMe
              );
            } catch (err) {
              console.warn("Unable to set session via SessionService:", err);
            }
          }
        }
        return res;
      })
    );
  }

  resendAdminLoginOtp(email: string) {
    return this.apiService.post<{ email: string }, any>({
      Url: "dating/auth/resend-admin-login-otp",
      Body: { email }
    });
  }
}
